import type {
  SchoolProfileRow,
  SchoolProfilesByType,
  SchoolProfilesCountryRow,
  SchoolProfilesMeta,
  SchoolProfilesSourceRow,
  SchoolProfilesViewsCountryRow,
  SchoolProfilesViewsKeyRow,
  SchoolProfileTierFilter,
  SchoolProfileViewsStatus,
} from '@/lib/types';
import type { BarListRow } from '../fab-ui';

/**
 * School Profiles: URL state, labels and the row builders the overview and
 * the table share. A school profile is a client school's public page on
 * SEED's Course Finder (Basic / Premium subscription); every figure is student
 * attention on it in the selected SEED cycle.
 *
 * URL state (every default is an absent key; nothing written on arrival):
 * | Key     | Meaning                                  | Default            |
 * |---------|------------------------------------------|--------------------|
 * | `cycle` | SEED business cycle id (SEED FAB's list) | SEED FAB's current |
 * | `tier`  | `basic` / `premium`                      | all                |
 * | `page`  | 1-based page of the school table         | 1                  |
 * | `tab`   | `schools` (the table); absent = Overview | Overview           |
 *
 * - Totals, interaction mix, countries and sources are the server's figures
 *   for the whole cycle and tier; only the school table pages.
 * - Nothing here sums across currencies or rounds a count: every figure is an
 *   exact integer copied off the wire.
 */

export const CYCLE_PARAM = 'cycle';
export const TIER_PARAM = 'tier';
export const PAGE_PARAM = 'page';
export const TAB_PARAM = 'tab';

export type SchoolProfilesTab = 'overview' | 'schools';

/** `schools` is the only written value; anything else is the Overview. */
export function parseTab(raw: string | null): SchoolProfilesTab {
  return raw === 'schools' ? 'schools' : 'overview';
}

/** Any filter the URL carries beyond the defaults (drives the chips and Reset). */
export function hasActiveFilters(urlCycleId: number | null, tier: SchoolProfileTierFilter): boolean {
  return urlCycleId !== null || tier !== 'all';
}

/** Upstream ceiling; the table pages at it. */
export const SCHOOL_PROFILES_PER_PAGE = 100;

export const TIER_OPTIONS: readonly { value: SchoolProfileTierFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'basic', label: 'Basic' },
  { value: 'premium', label: 'Premium' },
];

export function parseTier(raw: string | null): SchoolProfileTierFilter {
  return raw === 'basic' || raw === 'premium' ? raw : 'all';
}

export function parsePositiveInt(raw: string | null): number | null {
  if (raw === null || !/^\d+$/.test(raw)) return null;
  const n = Number.parseInt(raw, 10);
  return Number.isSafeInteger(n) && n > 0 ? n : null;
}

/** Plain labels for the interaction mix, in display order. */
export const INTERACTION_LABELS: readonly { key: keyof SchoolProfilesByType; label: string }[] = [
  { key: 'showInterest', label: 'Show interest' },
  { key: 'viewSchoolLead', label: 'Viewed lead form' },
  { key: 'scholarship', label: 'Scholarship' },
  { key: 'meetDirectors', label: 'Meet directors' },
  { key: 'favouritesCurrent', label: 'Favourites' },
  { key: 'sectionUnlocks', label: 'Section unlocks' },
  { key: 'programApplyNow', label: 'Apply clicks' },
];

function count(n: number | null | undefined): number {
  return n === null || n === undefined || !Number.isFinite(n) ? 0 : n;
}

/** Interaction mix rows, largest first; zero rows dropped. */
export function interactionRows(byType: SchoolProfilesByType | null | undefined): BarListRow[] {
  if (byType === null || byType === undefined) return [];
  return INTERACTION_LABELS.map(({ key, label }) => ({ key, label, value: count(byType[key]) }))
    .filter((row) => row.value > 0)
    .sort((a, b) => b.value - a.value);
}

export function countryRows(rows: readonly SchoolProfilesCountryRow[] | null | undefined): BarListRow[] {
  return (rows ?? [])
    .map((row, index) => ({
      key: row.countryCode ?? `unknown-${index}`,
      label: row.country ?? row.countryCode ?? 'Not stated',
      value: count(row.students),
    }))
    .sort((a, b) => b.value - a.value);
}

export function sourceRows(rows: readonly SchoolProfilesSourceRow[] | null | undefined): BarListRow[] {
  return (rows ?? [])
    .map((row, index) => {
      const source = row.utmSource !== null && row.utmSource.trim() !== '' ? row.utmSource : 'Direct';
      const medium = row.utmMedium !== null ? row.utmMedium.trim() : '';
      return {
        key: `${source}|${medium}|${index}`,
        label: source,
        value: count(row.students),
        ...(medium !== '' ? { hint: medium } : {}),
      };
    })
    .sort((a, b) => b.value - a.value);
}

export function programmeRows(school: SchoolProfileRow): BarListRow[] {
  return school.topPrograms.map((p, index) => ({
    key: `${p.programId ?? 'p'}-${index}`,
    label: p.name,
    value: count(p.students),
  }));
}

/**
 * Profile-view breakdowns (by visitor country, by channel / UTM source).
 * They render only when `meta.viewsBreakdownStatus === 'ok'`; any other
 * status, or an absent one (older API), renders nothing — no explanation.
 * A breakdown that is `undefined` is hidden; `null` renders as "—".
 */
export function viewsBreakdownOk(meta: SchoolProfilesMeta | null | undefined): boolean {
  return meta?.viewsBreakdownStatus === 'ok';
}

/** Plain channel labels; matched case- and separator-insensitively. */
const CHANNEL_LABELS: Record<string, string> = {
  internal: 'Internal',
  direct: 'Direct',
  referral: 'Referral',
  organic_search: 'Organic search',
  organic_social: 'Organic social',
  paid: 'Paid',
  email: 'Email',
};

const UNKNOWN_LABEL = 'Unknown';

export function channelLabel(key: string | null): string {
  if (key === null || key.trim() === '') return UNKNOWN_LABEL;
  const norm = key.trim().toLowerCase().replace(/[\s-]+/g, '_');
  return CHANNEL_LABELS[norm] ?? key.trim();
}

export function viewsCountryRows(
  rows: readonly SchoolProfilesViewsCountryRow[] | null | undefined,
): BarListRow[] {
  return (rows ?? [])
    .map((row, index) => ({
      key: row.countryCode ?? `unknown-${index}`,
      label: row.country ?? row.countryCode ?? UNKNOWN_LABEL,
      value: count(row.views),
    }))
    .sort((a, b) => b.value - a.value);
}

export function viewsChannelRows(
  rows: readonly SchoolProfilesViewsKeyRow[] | null | undefined,
): BarListRow[] {
  return (rows ?? [])
    .map((row, index) => ({
      key: `${row.key ?? 'unknown'}-${index}`,
      label: channelLabel(row.key),
      value: count(row.views),
    }))
    .sort((a, b) => b.value - a.value);
}

export function viewsUtmSourceRows(
  rows: readonly SchoolProfilesViewsKeyRow[] | null | undefined,
): BarListRow[] {
  return (rows ?? [])
    .map((row, index) => ({
      key: `${row.key ?? 'unknown'}-${index}`,
      label: row.key !== null && row.key.trim() !== '' ? row.key.trim() : UNKNOWN_LABEL,
      value: count(row.views),
    }))
    .sort((a, b) => b.value - a.value);
}

export const TIER_LABEL: Record<'basic' | 'premium', string> = { basic: 'Basic', premium: 'Premium' };

/** Why a school's views are blank, when they are. A fact about the data. */
export const VIEWS_STATUS_LABEL: Record<Exclude<SchoolProfileViewsStatus, 'ok'>, string> = {
  not_in_top_pages: 'Not in tracked pages',
  unavailable: 'Analytics unavailable',
  no_profile_path: 'No profile page',
};

export function schoolKey(school: SchoolProfileRow, index: number): string {
  return `${school.schoolId ?? 'x'}-${school.upstreamCompanyId ?? 'x'}-${index}`;
}
